import { FormValues } from "entities/appointment/lib/hooks/useAppointment";
import { bookingStore } from "entities/booking";
import { IBookingAppointment } from "entities/booking/store/types";
import { AppointmentForm, AppointmentInfo, DefaultService, PhoneModal, SuccessModal } from "features/appointment";
import { ServiceRadio } from "features/appointment/ui/ServiceRadio";
import { doctorsStore } from "features/doctors";
import { Doctor } from "features/doctors/store/types";
import { DoctorCardInfo } from "features/doctors/ui/doctorsCards/doctorCard/doctorCardInfo";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendPostMessage } from "shared/lib/messages";
import { FAILURE_TEXT, SUCCESS_TEXT } from "shared/consts";
import { ButtonBack } from "shared/ui/buttons/buttonBack";
import { clearLocalStorageExcept } from "shared/lib/storage";

export const Appointment = () => {

  const navigate = useNavigate();

  const [ doctor, setDoctor ] = useState<Doctor | null>(null);

  const [ phone, setPhone ] = useState<string>('')
  const [ modalTitle, setModalTitle ] = useState<string>('')
  const [ modalText, setModalText ] = useState<string>(SUCCESS_TEXT.replace(/\\n|[\n]/gm, '<br/>'))
  const [ buttonText, setButtonText ] = useState<string>('Продолжить')

  const [ isPhoneModalOpen, setIsPhoneModalOpen ] = useState<boolean>(true);
  const [ isLoading, setIsLoading ] = useState<boolean>(false);
  const [ isModalOpen, setIsModalOpen ] = useState<boolean>(false);
  const [ isSuccess, setIsSuccess ] = useState<boolean>(true);
  const [ serviceId, setServiceId ] = useState<number>(0);

  const onBackClick = () => {
    navigate(-1);
  }

  const checkPhone = useCallback(() => {
    let storePhone = bookingStore.getPhone();
    if (!storePhone) {
      storePhone = localStorage.getItem('phone');
    }

    if (!storePhone) {
      setIsPhoneModalOpen(true);
      return;
    }

    bookingStore.setPhone(storePhone);
    setPhone(storePhone);

    let isPhoneConfirmed = bookingStore.getIsPhoneConfirmed();
    if (!isPhoneConfirmed) {
      isPhoneConfirmed = localStorage.getItem('isPhoneConfirmed') === 'true';
    }

    if (!isPhoneConfirmed) {
      setIsPhoneModalOpen(true);
      return;
    }

    bookingStore.setIsPhoneConfirmed(isPhoneConfirmed);

    setIsPhoneModalOpen(false);
  }, [])

  useEffect(() => {
    const doctorId = bookingStore.getDoctorId();
    if (doctorId) {
      const doctor: Doctor = doctorsStore.getDoctorById(doctorId) as Doctor || doctorsStore.activeDoctor as Doctor;
      setDoctor(doctor);
      const service = doctor.services.length ? doctor.services[0].service.id : doctor.default_service?.id;
      setServiceId(service || 0);
    } else {
      navigate('/doctors-selection');
    }

    checkPhone();

  }, [navigate, checkPhone])

  const onSubmit = async (vals: FormValues) => {
    if (!bookingStore.getPhone() || !bookingStore.getTimeslot() || !doctor) return;

    const birth_day = [vals.birth_day.split('.')[2], vals.birth_day.split('.')[1], vals.birth_day.split('.')[0]];

    const payload: IBookingAppointment = {
      "first_name": vals.first_name,
      "last_name": vals.last_name,
      "middle_name": vals.middle_name,
      "birth_day": birth_day.join('-'),
      mobile_phone: '',
      timeslot: 0,
      services: serviceId ? [serviceId] : []
    }
    if (bookingStore.getPhone()) {
      payload.mobile_phone = bookingStore.getPhone()?.replace(/[^\d]/g, '');
    }
    if (bookingStore.getTimeslot()) {
      payload.timeslot = bookingStore.getTimeslot()?.id || 0;
    }

    setIsLoading(true);
    const answer = await bookingStore.sendAppointment(payload);

    if (!answer.results?.length) {
      setModalTitle('Ошибка');
      setModalText(FAILURE_TEXT.replace(/\\n|[\n]/gm, '<br/>'));
      setButtonText('Попробовать ещё');
      setIsModalOpen(true);
      setIsSuccess(false);
      setIsLoading(false);
      sendPostMessage({
        "event": "appointment",
        "success": false,
        "appointment": null
      })
      return;
    }

    const slot = bookingStore.getTimeslot();
    const date = slot?.start.split(' ')[0];
    const time = slot?.start.split(' ')[1];
    const staff = slot?.staff;
    const branch = slot?.branch;

    const message = {
      "event": "appointment",
      "success": true,
      "appointment": {
        staff,
        date,
        time,
        branch
      }
    }
    sendPostMessage(message);
    
    setModalTitle('');
    setModalText(SUCCESS_TEXT.replace(/\\n|[\n]/gm, '<br/>'));
    setButtonText('Продолжить');
    setIsLoading(false);
    setIsSuccess(true);
    setIsModalOpen(true);
  }

  const onSuccessClick = () => {
    setIsModalOpen(false);
    if (isSuccess) {
      bookingStore.setClinics([])
      const redirectPath = localStorage.getItem('redirect_path') || '/specializations';
      localStorage.removeItem('redirect_path')
      sendPostMessage({event: 'complete'});
      clearLocalStorageExcept(['phone', 'isPhoneConfirmed'])
      setTimeout(() => {
        window.open(redirectPath, '_self');
      }, 500);
    }
  }

  if (!doctor) {
    return <></>
  }

	return (
    <div>
      <ButtonBack 
        onClick={onBackClick}
      />
      <DoctorCardInfo 
        doctor={doctor}
      />

      {doctor.services?.length > 0 ? 
      doctor.services.map(service => (
        <ServiceRadio 
          service={service} 
          key={service.service.id} 
          isChecked={service.service.id === serviceId} 
          onClick={() => setServiceId(service.service.id)}
        />
      )) :
      (doctor.default_service && <DefaultService service={doctor.default_service} />)}

      {bookingStore.getTimeslot()?.start && 
        <AppointmentInfo 
        clinics={bookingStore.getClinicsNames()}
        start={bookingStore.getTimeslot()?.start}
        />
      }

      <AppointmentForm 
        phone={phone}
        onChangePhone={checkPhone}
        onSubmit={onSubmit}
        isLoading={isLoading}
      />

      <PhoneModal 
        isOpen={isPhoneModalOpen}
        onConfirm={checkPhone}
      />

      <SuccessModal 
        isOpen={isModalOpen}
        onClick={onSuccessClick}
        title={modalTitle}
        text={modalText}
        buttonText={buttonText}
      />
    </div>
  )
};
